// =============================================================================
// DroppableCell - Cella droppable con disponibilità a quarti d'ora
// =============================================================================

import { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '../../../lib/utils';
import { isResourceWorking } from '../../../utils/availabilityUtils';
import { QUARTER_OFFSETS } from '../utils/grid.constants';
import { useTenantTimeZone } from '../../../store/tenantStore';
import { civilDayOf, wallClockToUtc } from '../../../lib/tenantTime';
import type { DroppableCellProps } from '../types/scheduler.types';

export function DroppableCell({ resource, resourceId, exceptions, hour, dayStart, hoursCount, onClick }: DroppableCellProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `cell-${resourceId}-${hour}`,
    data: { resourceId, hour, dayStart },
  });

  const cellWidthPercent = 100 / hoursCount;
  // L'ora della colonna e' un orario di parete dell'organizzazione: l'istante
  // corrispondente dipende dal suo fuso, non da quello del browser.
  const timeZone = useTenantTimeZone();
  const cellTime = wallClockToUtc(civilDayOf(dayStart), hour * 60, timeZone);

  const quarterAvailability = useMemo(() => {
    return QUARTER_OFFSETS.map(minuteOffset => {
      const slotTime = new Date(cellTime.getTime() + minuteOffset * 60000);
      return isResourceWorking(resource, slotTime, exceptions, timeZone);
    });
  }, [resource, cellTime, exceptions, timeZone]);
  
  const hasAnyWorkingSlot = quarterAvailability.some(Boolean);
  const allWorking = quarterAvailability.every(Boolean);
  
  return (
    <div
      ref={setNodeRef}
      onClick={hasAnyWorkingSlot ? onClick : undefined}
      className={cn(
        'h-full border-r border-gray-100 transition-colors relative',
        hasAnyWorkingSlot 
          ? (isOver ? 'bg-blue-200/60 cursor-pointer' : 'hover:bg-blue-50/50 cursor-pointer')
          : 'cursor-not-allowed'
      )}
      style={{ width: `${cellWidthPercent}%` }}
      title={!hasAnyWorkingSlot ? 'Non operativo' : undefined}
    >
      {!allWorking && (
        <div className="absolute inset-0 flex flex-row h-full w-full pointer-events-none">
          {QUARTER_OFFSETS.map((offset, idx) => (
            <div
              key={offset}
              className={cn(
                'h-full flex-1 border-r border-gray-100/20 last:border-r-0',
                !quarterAvailability[idx] && 'bg-gray-100'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
